/**
 * ChatMessage 类型和 Schema 定义
 *
 * ⚠️ 注意: 此文件由脚本自动生成，请勿手动修改
 * 如需更新，请运行代码生成脚本
 */
import type { SchemaDefinition } from './_schema'
import type { ChatMessageAttachment } from './chat_message_attachment'

/**
 * ChatMessage
 */
export interface ChatMessage {
  /** [S] 消息ID */
  id?: string
  /** [S] Stream Channel ID（完整格式，含类型前缀） */
  cid?: string
  /** 频道类型（direct/camp/battle） */
  channel_type?: string
  /** [S] 频道ID（ChatChannel.id） */
  channel_id?: string
  /** [S] 消息文本内容 */
  text?: string
  /** 消息功能类型（可选值：regular=普通消息; error=错误消息; reply=回复消息; system=系统消息; ephemeral=临时消息; deleted=删除消息） */
  type?: string
  /** [S] 发送者ID */
  sender_id?: string
  /** [S] 附件列表（对齐 StreamChat SDK） */
  attachments?: ChatMessageAttachment[]
  /** [S] 父消息ID（线程回复） */
  parent_id?: string
  /** [S] 线程回复是否显示在主频道 */
  show_in_channel?: boolean
  /** [S] 回复数量 */
  reply_count?: number
  /** [S] 引用消息ID */
  quoted_message_id?: string
  /** [S] 被@用户ID列表 */
  mentioned_user_ids?: string[]
  /** [S] 限制可见的用户ID列表 */
  restricted_visibility?: string[]
  /** [S] 静默消息（不触发推送和未读） */
  silent?: boolean
  /** [S] 影子消息（仅发送者可见） */
  shadowed?: boolean
  /** [S] 斜杠命令 */
  command?: string
  /** [S] 是否置顶 */
  pinned?: boolean
  /** [S] 置顶时间 */
  pinned_at?: string
  /** [S] 置顶者ID */
  pinned_by_id?: string
  /** [S] 置顶过期时间 */
  pin_expires?: string
  /** [S] 创建时间（Stream 服务端） */
  created_at?: string
  /** [S] 更新时间 */
  updated_at?: string
  /** [S] 删除时间 */
  deleted_at?: string
  /** [S] 扩展数据 JSON */
  extra_data?: Record<string, any>
  /** [S] 投递状态 */
  delivery_status?: string
  /** [S] 投递错误信息 */
  delivery_error?: string
  /** [E] 内容类型 */
  content_type?: string
  /** [E] 系统消息子类型 */
  system_type?: string
  /** [E] 可见范围 */
  visible_scope?: string
  /** [E] 客户端本地消息ID */
  local_id?: string
  /** [C] 附件数量 */
  attachment_count?: number
  /** [C] Webhook 事件类型 */
  webhook_event?: string
  /** [C] Webhook 接收时间 */
  webhook_received_at?: string
  /** [D] 接收者ID（私聊特有） */
  receiver_id?: string
  /** [D] 接收方是否已读（私聊特有） */
  read_by_receiver?: boolean
  /** [D] 已读时间（私聊特有） */
  read_at?: string
  /** [G] 群组ID（群聊特有） */
  group_id?: string
  /** [G] 已读人数（群聊特有） */
  read_count?: number
  /** 战队方（可选值：red=红方; blue=蓝方; onlooker=围观） */
  battle_side?: string
  /** 对线生命周期阶段（可选值：preparing=准备中; battling=对战中; settling=结算中; finished=已结束） */
  battle_stage?: string
}

/**
 * ChatMessage Schema
 */
export const ChatMessageSchema: SchemaDefinition = {
  properties: {
    id: {
      description: '[S] 消息ID',
      format: 'string',
      type: 'string',
    },
    cid: {
      description: '[S] Stream Channel ID（完整格式，含类型前缀）',
      format: 'string',
      type: 'string',
    },
    channel_type: {
      description: '频道类型（direct/camp/battle）',
      format: 'cons.ChatChannelType',
      type: 'string',
      label: '频道类型',
    },
    channel_id: {
      description: '[S] 频道ID（ChatChannel.id）',
      format: 'string',
      type: 'string',
    },
    text: {
      description: '[S] 消息文本内容',
      format: 'string',
      type: 'string',
    },
    type: {
      description:
        '消息功能类型（可选值：regular=普通消息; error=错误消息; reply=回复消息; system=系统消息; ephemeral=临时消息; deleted=删除消息）',
      format: 'cons.ChatMessageType',
      type: 'string',
      label: '消息功能类型',
    },
    sender_id: {
      description: '[S] 发送者ID',
      format: 'string',
      type: 'string',
    },
    attachments: {
      description: '[S] 附件列表（对齐 StreamChat SDK）',
      format: '[]entity.ChatMessageAttachment',
      items: { $ref: 'ChatMessageAttachment', description: '' },
      type: 'array',
    },
    parent_id: {
      description: '[S] 父消息ID（线程回复）',
      format: 'string',
      type: 'string',
    },
    show_in_channel: {
      description: '[S] 线程回复是否显示在主频道',
      format: 'bool',
      type: 'boolean',
    },
    reply_count: {
      description: '[S] 回复数量',
      format: 'int',
      type: 'integer',
    },
    quoted_message_id: {
      description: '[S] 引用消息ID',
      format: 'string',
      type: 'string',
    },
    mentioned_user_ids: {
      description: '[S] 被@用户ID列表',
      format: '[]string',
      items: {
        format: 'string',
        type: 'string',
      },
      type: 'array',
    },
    restricted_visibility: {
      description: '[S] 限制可见的用户ID列表',
      format: '[]string',
      items: {
        format: 'string',
        type: 'string',
      },
      type: 'array',
    },
    silent: {
      description: '[S] 静默消息（不触发推送和未读）',
      format: 'bool',
      type: 'boolean',
    },
    shadowed: {
      description: '[S] 影子消息（仅发送者可见）',
      format: 'bool',
      type: 'boolean',
    },
    command: {
      description: '[S] 斜杠命令',
      format: 'string',
      type: 'string',
    },
    pinned: {
      description: '[S] 是否置顶',
      format: 'bool',
      type: 'boolean',
    },
    pinned_at: {
      description: '[S] 置顶时间',
      format: '*time.Time',
      type: 'string',
    },
    pinned_by_id: {
      description: '[S] 置顶者ID',
      format: 'string',
      type: 'string',
    },
    pin_expires: {
      description: '[S] 置顶过期时间',
      format: '*time.Time',
      type: 'string',
    },
    created_at: {
      description: '[S] 创建时间（Stream 服务端）',
      format: '*time.Time',
      type: 'string',
    },
    updated_at: {
      description: '[S] 更新时间',
      format: '*time.Time',
      type: 'string',
    },
    deleted_at: {
      description: '[S] 删除时间',
      format: '*time.Time',
      type: 'string',
    },
    extra_data: {
      additionalProperties: { type: 'any', description: '' },
      description: '[S] 扩展数据 JSON',
      format: 'map[string]interface {}',
      type: 'object',
    },
    delivery_status: {
      description: '[S] 投递状态',
      format: 'cons.ChatMessageDeliveryStatus',
      type: 'string',
    },
    delivery_error: {
      description: '[S] 投递错误信息',
      format: 'string',
      type: 'string',
    },
    content_type: {
      description: '[E] 内容类型',
      format: 'cons.ChatMessageContentType',
      type: 'string',
    },
    system_type: {
      description: '[E] 系统消息子类型',
      format: 'cons.ChatMessageSystemType',
      type: 'string',
    },
    visible_scope: {
      description: '[E] 可见范围',
      format: 'cons.ChatMessageVisibleScope',
      type: 'string',
    },
    local_id: {
      description: '[E] 客户端本地消息ID',
      format: 'string',
      type: 'string',
    },
    attachment_count: {
      description: '[C] 附件数量',
      format: 'int',
      type: 'integer',
    },
    webhook_event: {
      description: '[C] Webhook 事件类型',
      format: 'string',
      type: 'string',
    },
    webhook_received_at: {
      description: '[C] Webhook 接收时间',
      format: '*time.Time',
      type: 'string',
    },
    receiver_id: {
      description: '[D] 接收者ID（私聊特有）',
      format: 'string',
      type: 'string',
    },
    read_by_receiver: {
      description: '[D] 接收方是否已读（私聊特有）',
      format: 'bool',
      type: 'boolean',
    },
    read_at: {
      description: '[D] 已读时间（私聊特有）',
      format: '*time.Time',
      type: 'string',
    },
    group_id: {
      description: '[G] 群组ID（群聊特有）',
      format: 'string',
      type: 'string',
    },
    read_count: {
      description: '[G] 已读人数（群聊特有）',
      format: 'int',
      type: 'integer',
    },
    battle_side: {
      description: '战队方（可选值：red=红方; blue=蓝方; onlooker=围观）',
      format: 'cons.BattleSide',
      type: 'string',
      label: '战队方',
    },
    battle_stage: {
      description:
        '对线生命周期阶段（可选值：preparing=准备中; battling=对战中; settling=结算中; finished=已结束）',
      format: 'cons.BattleStage',
      type: 'string',
      label: '对线生命周期阶段',
    },
  },
  type: 'object',
}
